




import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import APIConfig from "../../Common/Configurations/APIConfig";
import Navbar from "../../Navigation/Navbar";
import Swal from "sweetalert2";
import { 
  Pencil, Trash2, X, Plus, Search, ChevronLeft, ChevronRight, 
  Map, Calendar, CreditCard, User, Landmark, Eye, RotateCcw, Loader2, FileText
} from "lucide-react";

const PaymentList = () => {
  const navigate = useNavigate();
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // UI Input States (What user sees/types)
  const [inputSearch, setInputSearch] = useState("");
  const [inputFromDate, setInputFromDate] = useState("");
  const [inputToDate, setInputToDate] = useState("");

  // Active Query States (What is actually sent to the API)
  const [activeSearch, setActiveSearch] = useState("");
  const [activeFromDate, setActiveFromDate] = useState("");
  const [activeToDate, setActiveToDate] = useState("");

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const pageSize = 10; 

  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);

  // This function only depends on the "Active" states and current page
  const fetchPayments = useCallback(async () => {
    setLoading(true);
    try {
      let url = `${APIConfig.APIACTIVEURL}${APIConfig.PAYMENT_GET_ALL}?pageNumber=${currentPage}&pageSize=${pageSize}&searchTerm=${activeSearch}`;
      
      if(activeFromDate) url += `&fromDate=${activeFromDate}`;
      if(activeToDate) url += `&toDate=${activeToDate}`;

      const response = await fetch(url);
      const result = await response.json();
      setPayments(result.data || []);
      setTotalPages(result.totalPages || 1);
    } catch (e) {
      console.error("Error fetching payments:", e);
    }
    setLoading(false);
  }, [currentPage, activeSearch, activeFromDate, activeToDate]);

  useEffect(() => {
    fetchPayments();
  }, [fetchPayments]);

  // TRIGGER SEARCH: Only updates active states when button is clicked
  const handleSearchButtonClick = () => {
    setActiveSearch(inputSearch);
    setActiveFromDate(inputFromDate);
    setActiveToDate(inputToDate);
    setCurrentPage(1); // Always reset to page 1 on new search
  };

  const handleClearSearch = () => {
    setInputSearch("");
    setInputFromDate("");
    setInputToDate("");
    setActiveSearch("");
    setActiveFromDate("");
    setActiveToDate("");
    setCurrentPage(1);
  };

  const handleDelete = async (id) => {
    Swal.fire({
      title: 'Are you sure?',
      text: "This payment record will be permanently deleted!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#ef4444',
      confirmButtonText: 'Yes, Delete'
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const res = await fetch(`${APIConfig.APIACTIVEURL}${APIConfig.PAYMENT_BASE}/${id}`, { method: "DELETE" });
          if(res.ok) {
            fetchPayments();
            Swal.fire('Deleted!', 'Payment has been deleted.', 'success');
          }
        } catch (err) { console.error(err); }
      }
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Completed': return { bg: '#dcfce7', text: '#166534' };
      case 'Pending': return { bg: '#fef9c3', text: '#854d0e' };
      case 'Bounced': return { bg: '#fee2e2', text: '#991b1b' };
      default: return { bg: '#f1f5f9', text: '#475569' };
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const d = new Date(dateString);
    return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
  };

  const styles = {
    container: { padding: "40px 60px", backgroundColor: "#f9fafc", minHeight: "100vh", fontFamily: "'Inter', sans-serif" },
    header: { display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginBottom: "35px" },
    title: { fontSize: "30px", fontWeight: "900", color: "#111827", margin: 0 },
    searchRow: { display: "flex", gap: "12px", marginBottom: "30px", alignItems: "center", flexWrap: "wrap" },
    searchBar: { position: "relative", flex: 1, minWidth: "300px" },
    input: { width: "100%", padding: "14px 16px 14px 45px", borderRadius: "14px", border: "1.5px solid #e2e8f0", outline: "none", fontSize: "14px", boxSizing: "border-box", backgroundColor:'#fff' },
    dateInput: { padding: "12px 15px", borderRadius: "14px", border: "1.5px solid #e2e8f0", outline: "none", fontSize: "14px", backgroundColor: "#fff", color: "#1b2559", fontWeight: "600" },
    dateLabel: { fontSize: "12px", fontWeight: "800", color: "#64748b", textTransform: "uppercase", marginBottom: "4px" },
    btnPrimary: { backgroundColor: "#4f46e5", color: "#fff", border: "none", padding: "14px 28px", borderRadius: "14px", cursor: "pointer", fontWeight: "700", display: "flex", alignItems: "center", gap: "8px" },
    btnGhost: { backgroundColor: "#fff", color: "#64748b", padding: "14px 24px", borderRadius: "14px", border: "1.5px solid #e2e8f0", fontWeight: "600", cursor: "pointer", display: "flex", alignItems: "center", gap: "8px" },
    addBtn: { backgroundColor: "#2f54c3", color: "#fff", border: "none", padding: "14px 24px", borderRadius: "14px", cursor: "pointer", fontWeight: "700", display: "flex", alignItems: "center", gap: "8px" },
    tableWrapper: { backgroundColor: "#fff", borderRadius: "24px", boxShadow: "0 1px 3px rgba(0,0,0,0.1)", border: "1px solid #f1f5f9", overflow: "hidden" },
    th: { padding: "18px 24px", backgroundColor: "#f8fafc", color: "#64748b", fontSize: "12px", fontWeight: "800", textTransform: "uppercase", borderBottom: "1px solid #edf2f7", textAlign: "left" },
    td: { padding: "20px 24px", borderBottom: "1px solid #f8fafc", verticalAlign: "middle" },
    modalOverlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000, backdropFilter: 'blur(6px)' },
    modalContent: { backgroundColor: '#fff', borderRadius: '24px', width: '90%', maxWidth: '800px', maxHeight: '85vh', overflowY: 'auto', padding: '30px', position: 'relative' },
    sectionTitle: { fontSize: "18px", fontWeight: "800", color: "#1b2559", display: "flex", alignItems: "center", gap: "8px", borderBottom: "2px solid #f1f5f9", paddingBottom: "10px", marginBottom: "15px", marginTop: "20px" },
    grid: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: "20px" },
    dataRow: { display: "flex", flexDirection: "column", gap: "4px" },
    label: { fontSize: "12px", color: "#94a3b8", fontWeight: "600", textTransform: "uppercase" },
    value: { fontSize: "15px", color: "#1b2559", fontWeight: "700" }
  };

  return (
    <>
      <Navbar />
      <div style={styles.container}>
        <div style={styles.header}>
          <h1 style={styles.title}>Payment History</h1>
          <button style={styles.addBtn} onClick={() => navigate("/payments/manage")}>
            <Plus size={20} /> Add New Payment
          </button>
        </div>

        {/* SEARCH & DATE FILTERS ROW */}
        <div style={styles.searchRow}>
          <div style={styles.searchBar}>
            <Search size={18} style={{ position: "absolute", left: "15px", top: "15px", color: "#cbd5e1" }} />
            <input 
              style={styles.input} 
              placeholder="Search by member name, phone, or receipt..." 
              value={inputSearch}
              onChange={(e) => setInputSearch(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearchButtonClick()}
            />
          </div>
          <div style={{display:'flex', flexDirection:'column'}}>
            <label style={styles.dateLabel}>From Date</label>
            <input type="date" style={styles.dateInput} value={inputFromDate} onChange={(e) => setInputFromDate(e.target.value)} />
          </div>
          <div style={{display:'flex', flexDirection:'column'}}>
            <label style={styles.dateLabel}>To Date</label>
            <input type="date" style={styles.dateInput} value={inputToDate} onChange={(e) => setInputToDate(e.target.value)} />
          </div>
          <div style={{display:'flex', gap:'10px', marginTop: 'auto'}}>
            <button style={styles.btnPrimary} onClick={handleSearchButtonClick}>Search</button>
            <button style={styles.btnGhost} onClick={handleClearSearch}><RotateCcw size={18}/> Reset</button>
          </div>
        </div>

        <div style={styles.tableWrapper}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th style={{ ...styles.th, width: "20%" }}>Date & Type</th>
                <th style={{ ...styles.th, width: "25%" }}>Member & Plot</th>
                <th style={{ ...styles.th, width: "25%" }}>Amount & Receipt</th>
                <th style={{ ...styles.th, width: "15%" }}>Status</th>
                <th style={{ ...styles.th, textAlign: "center", width: "15%" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="5" style={{ padding: "100px", textAlign: "center" }}><Loader2 className="animate-spin" color="#4f46e5" /></td></tr>
              ) : payments.length === 0 ? (
                <tr><td colSpan="5" style={{ padding: "80px", textAlign: "center", color: "#94a3b8" }}>No payments found in the system.</td></tr>
              ) : payments.map((p) => {
                const statusTheme = getStatusColor(p.status);
                return (
                  <tr key={p.paymentId}>
                    <td style={styles.td}>
                      <div style={{ fontWeight: "800", color: "#1b2559", fontSize:'15px', display: 'flex', alignItems: 'center', gap: '6px' }}><Calendar size={15} color="#4f46e5" /> {formatDate(p.paymentDate)}</div>
                      <div style={{ fontSize: "12px", color: "#64748b", marginTop: "4px", fontWeight: "700" }}>{p.paymentType}</div>
                    </td>
                    <td style={styles.td}>
                      <div style={{ fontSize: "14px", display:'flex', alignItems:'center', gap:'6px', color:'#1b2559', fontWeight: '800' }}>
                        <User size={15} color="#94a3b8"/> {p.member?.memberName} ({p.member?.phoneNo || "N/A"})
                      </div>
                      <div style={{ fontSize: "12px", display:'flex', alignItems:'center', gap:'6px', color:'#64748b', marginTop:'6px', fontWeight:'600' }}><Map size={14} color="#4f46e5"/> {p.plot ? `${p.plot.plotNumber} (${p.plot.project?.projectName || 'N/A'})` : 'General Payment'}</div>
                    </td>
                    <td style={styles.td}>
                      <div style={{ fontSize: "16px", color: "#1b2559", fontWeight: "900", marginBottom: "4px" }}>₹ {p.amountPaid.toLocaleString()}</div>
                      <div style={{ fontSize: "12px", color: "#4f46e5", display: 'flex', alignItems: 'center', gap: '6px', fontWeight: '800' }}><FileText size={13}/> {p.receiptNo || "No Receipt"}</div>
                    </td>
                    <td style={styles.td}><div style={{ padding: "6px 12px", borderRadius: "8px", fontSize: "11px", fontWeight: "800", backgroundColor: statusTheme.bg, color: statusTheme.text, display:'inline-block', textTransform:'uppercase' }}>{p.status}</div></td>
                    <td style={{ ...styles.td, textAlign: "center" }}>
                      <div style={{display:'flex', justifyContent:'center', gap:'10px'}}>
                          <button onClick={() => {setSelectedPayment(p); setIsViewModalOpen(true);}} style={{border:'none', background:'#f1f5f9', padding:'8px', borderRadius:'10px', cursor:'pointer'}}><Eye size={18}/></button>
                          <button onClick={() => navigate(`/payments/manage?id=${p.paymentId}`)} style={{border:'none', background:'#eef2ff', padding:'8px', borderRadius:'10px', cursor:'pointer'}}><Pencil size={18}/></button>
                          <button onClick={() => handleDelete(p.paymentId)} style={{border:'none', background:'#fff1f2', padding:'8px', borderRadius:'10px', cursor:'pointer'}}><Trash2 size={18}/></button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <div style={{ padding: "24px", display: "flex", justifyContent: "space-between", alignItems: "center", borderTop: "1px solid #f4f7fe" }}>
            <span style={{ fontSize: "14px", color: "#94a3b8", fontWeight: "700" }}>Page {currentPage} of {totalPages}</span>
            <div style={{ display: "flex", gap: "10px" }}>
              <button disabled={currentPage === 1} onClick={() => setCurrentPage(c => c - 1)} style={styles.btnGhost}><ChevronLeft size={20}/></button>
              <button disabled={currentPage === totalPages} onClick={() => setCurrentPage(c => c + 1)} style={styles.btnGhost}><ChevronRight size={20}/></button>
            </div>
          </div>
        </div>
      </div>

      {/* --- POPUP MODAL (100% PRESERVED DESIGN) --- */}
      {isViewModalOpen && selectedPayment && (
        <div style={styles.modalOverlay}>
          <div style={styles.modalContent}>
            <button onClick={() => setIsViewModalOpen(false)} style={{ position: 'absolute', right: '20px', top: '20px', background: '#f1f5f9', border: 'none', cursor: 'pointer', color: '#64748b', padding: '10px', borderRadius: '50%' }}><X size={20} /></button>
            <h3 style={{ fontSize: '24px', fontWeight: '900', color: '#1b2559', margin: '0 0 5px 0' }}>Transaction Details</h3>
            
            <h4 style={styles.sectionTitle}><CreditCard size={18} color="#4f46e5" /> Financial Details</h4>
            <div style={{ ...styles.grid, backgroundColor: '#f8fafc', padding: '25px', borderRadius: '20px', border: '1px solid #eef2f6' }}>
              <div style={styles.dataRow}><span style={styles.label}>Amount Paid</span><span style={{...styles.value, fontSize: '20px', color: '#10b981'}}>₹ {selectedPayment.amountPaid?.toLocaleString()}</span></div>
              <div style={styles.dataRow}><span style={styles.label}>Receipt Number</span><span style={{...styles.value, color: '#4f46e5'}}>{selectedPayment.receiptNo || "N/A"}</span></div>
              <div style={styles.dataRow}><span style={styles.label}>Date of Payment</span><span style={styles.value}>{formatDate(selectedPayment.paymentDate)}</span></div>
              <div style={styles.dataRow}><span style={styles.label}>Category</span><span style={styles.value}>{selectedPayment.paymentType}</span></div>
              <div style={styles.dataRow}><span style={styles.label}>Mode of Payment</span><span style={styles.value}>{selectedPayment.paymentMethod}</span></div>
              <div style={styles.dataRow}><span style={styles.label}>Bank / Institution</span><span style={styles.value}>{selectedPayment.bankName || "N/A"}</span></div>
              <div style={styles.dataRow}><span style={styles.label}>Reference / ID</span><span style={styles.value}>{selectedPayment.transactionReference || "N/A"}</span></div>
              <div style={styles.dataRow}><span style={styles.label}>Transaction Status</span><span style={{...styles.value, color: selectedPayment.status === 'Completed' ? '#166534' : '#991b1b'}}>{selectedPayment.status}</span></div>
              <div style={{...styles.dataRow, gridColumn: '1 / -1'}}><span style={styles.label}>Internal Remarks</span><span style={styles.value}>{selectedPayment.remarks || "No remarks provided"}</span></div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '25px', marginTop: '20px' }}>
              <div>
                <h4 style={styles.sectionTitle}><User size={18} color="#4f46e5" /> Member Contact</h4>
                <div style={styles.dataRow}><span style={styles.label}>Primary Name</span><span style={styles.value}>{selectedPayment.member?.memberName}</span></div>
                <div style={styles.dataRow}><span style={styles.label}>Mobile Link</span><span style={styles.value}>{selectedPayment.member?.phoneNo}</span></div>
              </div>
              <div>
                <h4 style={styles.sectionTitle}><Map size={18} color="#4f46e5" /> Plot Association</h4>
                {selectedPayment.plot ? (
                  <>
                    <div style={styles.dataRow}><span style={styles.label}>Plot Identifier</span><span style={styles.value}>{selectedPayment.plot.plotNumber}</span></div>
                    <div style={styles.dataRow}><span style={styles.label}>Project Title</span><span style={styles.value}>{selectedPayment.plot.project?.projectName}</span></div>
                  </>
                ) : <div style={{color: '#94a3b8', fontSize: '14px', marginTop: '10px'}}>General Balance Payment</div>}
              </div>
            </div>
            <button onClick={() => setIsViewModalOpen(false)} style={{ ...styles.btnPrimary, width: "100%", justifyContent: "center", marginTop: "35px" }}>Close Summary</button>
          </div>
        </div>
      )}
    </>
  );
};

export default PaymentList;